/**
 * Salvarea automata a editorului: dupa `delayMs` de liniste se trimite
 * ultima stare; la `pagehide` (fila inchisa, telefonul care schimba
 * aplicatia) se trimite IMEDIAT ce a ramas, cu `keepalive`, ca cererea sa
 * supravietuiasca paginii.
 *
 * O singura cerere pe drum odata: ce se schimba cat timp una e pe drum
 * pleaca dupa ea, nu in paralel - altfel o stare veche ar putea ajunge
 * ultima si ar calca una noua.
 */
export type SaveFn<T> = (state: T, opts: { keepalive: boolean }) => Promise<void>

export type Autosave<T> = {
  schedule(state: T): void
  flush(opts?: { keepalive?: boolean }): Promise<void>
  pending(): boolean
  dispose(): void
}

export function createAutosave<T>(
  save: SaveFn<T>,
  delayMs = 1200,
  onError: (e: unknown) => void = () => {},
  target: Pick<Window, 'addEventListener' | 'removeEventListener'> | null = typeof window === 'undefined' ? null : window,
): Autosave<T> {
  let waiting: { state: T } | null = null
  let timer: ReturnType<typeof setTimeout> | null = null
  let inflight: Promise<void> | null = null

  const run = (keepalive: boolean): Promise<void> => {
    if (timer !== null) {
      clearTimeout(timer)
      timer = null
    }
    if (waiting === null) return inflight ?? Promise.resolve()
    if (inflight !== null && !keepalive) {
      /* Dupa cea de pe drum. */
      return inflight.then(() => run(false))
    }
    const { state } = waiting
    waiting = null
    const p = save(state, { keepalive })
      .catch((e: unknown) => onError(e))
      .finally(() => {
        if (inflight === p) inflight = null
      })
    inflight = p
    return p
  }

  const onHide = () => {
    void run(true)
  }
  target?.addEventListener('pagehide', onHide)

  return {
    schedule(state: T) {
      waiting = { state }
      if (timer !== null) clearTimeout(timer)
      timer = setTimeout(() => void run(false), delayMs)
    },
    flush(opts) {
      return run(Boolean(opts?.keepalive))
    },
    pending() {
      return waiting !== null || inflight !== null
    },
    dispose() {
      target?.removeEventListener('pagehide', onHide)
      void run(true)
    },
  }
}
